import React from 'react';
import { ChevronRight, CircleMinus } from '@menuzen/icon/build/outline';
import { DraggableProvided } from 'react-beautiful-dnd';
import { Box, Switch, CSSObject } from '@mui/material';
import classNames from 'classnames';
import DehazeIcon from '@mui/icons-material/Dehaze';

import { useEditor } from '@mnz/builder';
import { LabelIcon } from '@mnz/element';
import { BLOCKS_MODE } from '../config';
import { capitalizeFirstLetter } from '../../../../shared/utils/string';
import { CustomLinkProps } from '@mnz/element/build/basic/custom-link';
import { useQueryParam } from '../../../../common/hooks/use-query-param';

interface BlockCardProps {
    nodeId: string;
    mode?: string;
    isRequiredBlock?: boolean;
    onDelete?: () => void;
    isAlwaysShow?: boolean;
    provided?: DraggableProvided;
    dragCss?: CSSObject;
}

const BlockCard: React.FC<BlockCardProps> = ({
    nodeId,
    isRequiredBlock = false,
    isAlwaysShow = false,
    mode,
    onDelete,
    provided,
    dragCss,
}) => {
    const {
        actions: { selectNode, setHidden },
        nodeData,
        customLinkData,
    } = useEditor((_, query) => {
        const nodes = query.getNodes();
        let customLinkData = null;
        if (nodes[nodeId].data.name === 'CustomLink') {
            const props = nodes[nodeId].data.props as unknown as CustomLinkProps;
            customLinkData = {
                text: props.text,
                link: props.link,
            };
        }
        return {
            nodeData: nodes[nodeId],
            customLinkData,
        };
    });
    const [, setModal] = useQueryParam<string | null>('modal');
    const onOpenSettingNode = () => {
        if (mode !== BLOCKS_MODE.EDIT) {
            selectNode(nodeId);
            setModal(null);
        }
    };
    const setHiddenNode = (value: boolean) => {
        setHidden(nodeId, value);
    };

    return (
        <Box ref={provided?.innerRef} {...provided?.draggableProps} {...provided?.dragHandleProps} className="my-4">
            <Box
                sx={{
                    ...dragCss,
                }}
                className={classNames([
                    'flex items-center justify-center border border-primary-40 rounded-8 py-10 px-12 bg-white',
                ])}
            >
                {provided && (
                    <div className="text-16 mr-8">
                        <DehazeIcon sx={{ color: (theme) => theme.palette.primary50, fontSize: '16px' }} />
                    </div>
                )}
                <div
                    className="shrink-0 rounded-5 bg-started h-40 w-40 flex items-center justify-center text-20 cursor-pointer"
                    onClick={onOpenSettingNode}
                >
                    <LabelIcon type={'outline'} icon={capitalizeFirstLetter(nodeData.data.name)} size={20} color={``} />
                </div>
                <div className="flex-grow mx-8 cursor-pointer min-w-0" onClick={onOpenSettingNode}>
                    {customLinkData && (
                        <>
                            <div className="text-14 truncate">
                                {customLinkData.text ? customLinkData.text : customLinkData.link}
                            </div>
                            <div className="mt-2 text-10 leading-12 text-primary-70">{`Custom Link`}</div>
                        </>
                    )}
                    {!customLinkData && (
                        <>
                            <div className="text-14">{nodeData.data.displayName}</div>
                        </>
                    )}
                </div>
                {mode === BLOCKS_MODE.EDIT && !isRequiredBlock ? (
                    <button className="text-16 mr-8" onClick={onDelete}>
                        <CircleMinus />
                    </button>
                ) : (
                    <div className="flex items-center justify-center">
                        {!isAlwaysShow && nodeData.data.name !== 'BasicInfo' && (
                            <Switch
                                checked={!nodeData.data.hidden}
                                onChange={(_, checked) => setHiddenNode(!checked)}
                                disabled={mode === BLOCKS_MODE.EDIT && isRequiredBlock}
                            />
                        )}
                        <div className="text-20 text-primary-50 ml-8 cursor-pointer" onClick={onOpenSettingNode}>
                            <ChevronRight />
                        </div>
                    </div>
                )}
            </Box>
        </Box>
    );
};

export default BlockCard;
